import { CookieKey, DragDropEngine } from 'enums/cookies.enums';
import {
  DEFAULT_APP_SETTINGS,
  IAppCookie,
  PaginationID,
} from 'interfaces/cookies/i-app.cookie';
import {
  DEFAULT_SNAPSHOT_COOKIE,
  ISnapshotCookie,
} from 'interfaces/cookies/i-snapshot.cookie';
import {
  DEFAULT_AUTH_SESSION,
  IAuthSessionCookie,
  ILoginSuccess,
  ISessionCurrent,
} from 'lib_ts/interfaces/common/i-login';
import Cookies from 'universal-cookie';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

const COOKIES_PATH = { path: '/' };
const cookies = new Cookies();

type ValidCookie =
  | Partial<IAppCookie>
  | Partial<ISnapshotCookie>
  | Partial<ISessionCurrent>;

type SetCookieOptions = {
  expires?: Date;
  loginEvent?: boolean;
};

type CookiesState = {
  app: IAppCookie;
  authSession: IAuthSessionCookie;
  snapshot: ISnapshotCookie;
  init: boolean;
};

export type CookiesActions = {
  setPageSize: (id: PaginationID, value: number) => void;
  getPageSize: (id: PaginationID) => number | undefined;

  setAuthSessionCookie: (loginResponse: ILoginSuccess) => void;

  setCookie: (
    key: CookieKey,
    value: ValidCookie,
    options?: SetCookieOptions
  ) => Promise<boolean>;
  removeCookie: (key: CookieKey) => boolean;
};

type CookiesStore = CookiesState & CookiesActions;

/** to save separate entries in the cookie for each combination of user + table identifier */
const getSessionTableKey = (email: string, id: PaginationID) =>
  `${email || 'anonymous'}-${id}`;

const getSafeDragDropEngine = (value: DragDropEngine): DragDropEngine => {
  if (Object.values(DragDropEngine).includes(value)) {
    return value;
  }
  return DEFAULT_APP_SETTINGS.drag_drop_engine;
};

export const useCookiesStore = create(
  subscribeWithSelector<CookiesStore>((set, get) => ({
    app: {
      ...DEFAULT_APP_SETTINGS,
      ...cookies.get(CookieKey.app),
    },
    authSession: {
      ...DEFAULT_AUTH_SESSION,
      ...cookies.get(CookieKey.authSession),
    },
    snapshot: {
      ...DEFAULT_SNAPSHOT_COOKIE,
      ...cookies.get(CookieKey.snapshot),
    },
    init: false,

    setPageSize: (id, value) => {
      const { app, authSession } = get();
      const key = getSessionTableKey(authSession.email, id);
      const nextApp: IAppCookie = {
        ...app,
        page_sizes: {
          ...app.page_sizes,
          [key]: value,
        },
      };

      cookies.set(CookieKey.app, nextApp, COOKIES_PATH);
      set({ app: nextApp });
    },

    getPageSize: (id) => {
      const { app, authSession } = get();
      return app.page_sizes[getSessionTableKey(authSession.email, id)];
    },

    setCookie: async (key, value, options) => {
      try {
        const { app, authSession, snapshot } = get();

        switch (key) {
          case CookieKey.app: {
            const nextValue: IAppCookie = {
              ...app,
              ...value,
            };

            // guard again legacy/invalid DragDropEngine value
            nextValue.drag_drop_engine = getSafeDragDropEngine(
              nextValue.drag_drop_engine
            );

            cookies.set(CookieKey.app, nextValue, COOKIES_PATH);
            set({ app: nextValue });
            break;
          }

          case CookieKey.authSession: {
            const nextValue: IAuthSessionCookie = {
              ...authSession,
              ...value,
            };

            cookies.set(CookieKey.authSession, nextValue, {
              expires: options?.expires,
              ...COOKIES_PATH,
            });

            set({ authSession: nextValue });
            break;
          }

          case CookieKey.snapshot: {
            const nextValue: ISnapshotCookie = {
              ...snapshot,
              ...value,
            };
            cookies.set(CookieKey.snapshot, nextValue, {
              path: '/',
              ...(options?.expires ? { expires: options.expires } : {}),
            });
            set({ snapshot: nextValue });
            break;
          }

          default:
            // no-op
            break;
        }

        return true;
      } catch (e) {
        console.error(e);
        return false;
      }
    },

    removeCookie: (key) => {
      try {
        switch (key) {
          case CookieKey.app: {
            cookies.remove(CookieKey.app, COOKIES_PATH);
            set({ app: DEFAULT_APP_SETTINGS });
            break;
          }
          case CookieKey.authSession: {
            cookies.remove(CookieKey.authSession, COOKIES_PATH);
            set({ authSession: DEFAULT_AUTH_SESSION });
            break;
          }
          case CookieKey.snapshot: {
            cookies.remove(CookieKey.snapshot, COOKIES_PATH);
            set({ snapshot: DEFAULT_SNAPSHOT_COOKIE });
            break;
          }
          default:
            break;
        }
        return true;
      } catch (e) {
        console.error(e);
        return false;
      }
    },

    // takes only bare necessities needed from a login response and saves it to a cookie so we know if the user is logged in or not
    setAuthSessionCookie: (value) => {
      const { authSession } = get();

      const nextValue: IAuthSessionCookie = {
        ...authSession,
        email: value.email,
        method: value.method,
        auth: true,
      };

      cookies.set(CookieKey.authSession, nextValue, {
        expires: new Date(value.refreshExpiresAt),
        ...COOKIES_PATH,
      });

      set({ authSession: nextValue });
    },
  }))
);
